import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import {
  normalizeCoordinateInput,
  normalizeStateCodeInput,
  setNormalizedFormikValue,
} from 'client/helpers/formInputNormalization';

/**
 * Get form fields based on current mode
 * @param {boolean} isManualCreationForm - false = autocomplete, true = manual
 * @param {Function} setIsManualCreationForm - State setter for form mode
 * @param {boolean} disabled - Whether the form is currently submitting
 */
const getFormFields = ({
  isManualCreationForm,
  setIsManualCreationForm,
  disabled,
  formikBag,
  allowToggle,
}) => {
  if (!isManualCreationForm) {
    return [
      {
        id: 'addressString',
        title: 'New address *',
        titleToggle: allowToggle
          ? {
              text: "Can't find address?",
              onClick: disabled ? undefined : () => setIsManualCreationForm(true),
            }
          : null,
        type: 'smarty_address_autocomplete',
        properties: {},
        validations: ['required'],
      },
    ];
  }

  return [
    {
      id: 'streetNumber',
      title: 'Street Number *',
      titleToggle: allowToggle
        ? {
            text: 'Use address search?',
            onClick: disabled ? undefined : () => setIsManualCreationForm(false),
          }
        : null,
      type: 'short_text',
      properties: {
        placeholder: 'e.g., 123',
        onlyAcceptNumbers: true,
      },
      validations: ['required'],
    },
    {
      id: 'streetName',
      title: 'Street Name *',
      type: 'short_text',
      properties: {
        placeholder: 'e.g., Main',
      },
      validations: ['required'],
    },
    {
      id: 'streetPrefix',
      title: 'Street Prefix',
      type: 'short_text',
      properties: {
        placeholder: 'e.g., N',
      },
    },
    {
      id: 'streetSuffix',
      title: 'Street Suffix',
      type: 'short_text',
      properties: {
        placeholder: 'e.g., St',
      },
    },
    {
      id: 'unitNumber',
      title: 'Unit Number',
      type: 'short_text',
      properties: {
        placeholder: 'e.g., Apt 4B',
      },
    },
    {
      id: 'city',
      title: 'City *',
      type: 'short_text',
      properties: {
        placeholder: 'e.g., Raleigh',
      },
      validations: ['required'],
    },
    {
      id: 'stateCode',
      title: 'State *',
      type: 'short_text',
      tooltipCopy: 'Use 2-letter state code (e.g., NC).',
      properties: {
        placeholder: 'e.g., NC',
        onChange: (event) => {
          setNormalizedFormikValue({
            formikBag,
            fieldId: 'stateCode',
            value: event.target.value || '',
            normalizer: normalizeStateCodeInput,
          });
        },
      },
      validations: ['required', 'stateCode'],
    },
    {
      id: 'zip',
      title: 'Zip Code *',
      type: 'zipCode',
      properties: {
        placeholder: 'e.g., 27601',
      },
      validations: ['required', 'zipCode'],
    },
    {
      id: 'county',
      title: 'County',
      type: 'short_text',
      properties: {
        placeholder: 'e.g., Wake',
      },
    },
    {
      id: 'latitude',
      title: 'Latitude *',
      type: 'short_text',
      properties: {
        type: 'number',
        step: 'any',
        placeholder: 'e.g., 35.7796',
        onChange: (event) => {
          setNormalizedFormikValue({
            formikBag,
            fieldId: 'latitude',
            value: event.target.value || '',
            normalizer: normalizeCoordinateInput,
          });
        },
      },
      validations: ['required', 'latitude'],
    },
    {
      id: 'longitude',
      title: 'Longitude *',
      type: 'short_text',
      properties: {
        type: 'number',
        step: 'any',
        placeholder: 'e.g., -78.6382',
        onChange: (event) => {
          setNormalizedFormikValue({
            formikBag,
            fieldId: 'longitude',
            value: event.target.value || '',
            normalizer: normalizeCoordinateInput,
          });
        },
      },
      validations: ['required', 'longitude'],
    },
  ];
};

export default function UPAddressVerificationForm({
  disabled,
  onRegister,
  initialAddressFields,
  forceManual,
  submitLabel,
  submittingLabel,
}) {
  // Form mode state: false = autocomplete, true = manual
  const [isManualCreationForm, setIsManualCreationForm] = useState(Boolean(forceManual));
  const allowToggle = !forceManual;

  useEffect(() => {
    if (forceManual) {
      setIsManualCreationForm(true);
    }
  }, [forceManual]);

  const normalizedInitialAddressFields = initialAddressFields || {};

  // Initial form values
  const initialValues = createInitialValues({
    formFields: getFormFields({
      isManualCreationForm,
      setIsManualCreationForm,
      disabled,
      allowToggle,
    }),
    defaultValues: {
      addressString: '',
      streetNumber: normalizedInitialAddressFields.streetNumber || '',
      streetName: normalizedInitialAddressFields.streetName || '',
      streetPrefix: normalizedInitialAddressFields.streetPrefix || '',
      streetSuffix: normalizedInitialAddressFields.streetSuffix || '',
      city: normalizedInitialAddressFields.city || '',
      stateCode: normalizedInitialAddressFields.stateCode || '',
      zip: normalizedInitialAddressFields.zip || '',
      county: normalizedInitialAddressFields.county || '',
      unitNumber: normalizedInitialAddressFields.unitNumber || '',
      latitude:
        normalizedInitialAddressFields.latitude === null ||
        normalizedInitialAddressFields.latitude === undefined
          ? ''
          : normalizedInitialAddressFields.latitude,
      longitude:
        normalizedInitialAddressFields.longitude === null ||
        normalizedInitialAddressFields.longitude === undefined
          ? ''
          : normalizedInitialAddressFields.longitude,
    },
  });

  // Handle form submission
  const handleSubmit = (formValues, formikBag) => {
    if (!isManualCreationForm) {
      onRegister({
        addressString: formValues.addressString,
        onSuccess: () => formikBag.resetForm(),
      });
      return;
    }

    const addressFieldsPayload = {
      streetNumber: formValues.streetNumber,
      streetName: formValues.streetName,
      streetPrefix: formValues.streetPrefix,
      streetSuffix: formValues.streetSuffix,
      unitNumber: formValues.unitNumber,
      city: formValues.city,
      stateCode: formValues.stateCode ? formValues.stateCode.toUpperCase() : formValues.stateCode,
      zip: formValues.zip,
      county: formValues.county,
      latitude: formValues.latitude,
      longitude: formValues.longitude,
      countryCode: normalizedInitialAddressFields.countryCode || 'US',
    };

    onRegister({
      addressFields: addressFieldsPayload,
      onSuccess: () => formikBag.resetForm(),
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={createFormValidationFn(
        getFormFields({ isManualCreationForm, setIsManualCreationForm, disabled, allowToggle }),
      )}
      onSubmit={handleSubmit}
      render={(formikBag) => {
        const formFields = getFormFields({
          isManualCreationForm,
          setIsManualCreationForm,
          disabled,
          formikBag,
          allowToggle,
        });

        return (
          <Form>
            {/* Dynamic form fields */}
            {formFields.map((formField) => (
              <SrBox key={formField.id} marginBottom="16px">
                <SrFormikField
                  key={`field-${formField.id}`}
                  formField={formField}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                  disabled={disabled}
                />
              </SrBox>
            ))}

            {/* Action buttons */}
            <SrBox paddingTop="12px">
              <SrFlex direction="col" gap="16px">
                <SrButton theme={SR_BUTTON_THEME_ADOBE_GREEN} disabled={disabled} type="submit">
                  {disabled ? submittingLabel : submitLabel}
                </SrButton>
              </SrFlex>
            </SrBox>
          </Form>
        );
      }}
    />
  );
}

UPAddressVerificationForm.propTypes = {
  disabled: PropTypes.bool,
  onRegister: PropTypes.func,
  initialAddressFields: PropTypes.object,
  forceManual: PropTypes.bool,
  submitLabel: PropTypes.string,
  submittingLabel: PropTypes.string,
};

UPAddressVerificationForm.defaultProps = {
  disabled: false,
  onRegister: () => {},
  initialAddressFields: null,
  forceManual: false,
  submitLabel: 'Link Address',
  submittingLabel: 'Linking...',
};
